import { useState, useRef, useEffect } from 'react';
import { ChatContextMenu } from './ChatContextMenu';

interface ChatSidebarItemProps {
  conversationId: string;
  title: string;
  meta: string;
  active: boolean;
  unreadCount: number;
  isPinned: boolean;
  isGroupChat: boolean;
  canRename: boolean;
  isMenuOpen: boolean;
  isRenaming: boolean;
  projectId?: string | null;
  projectName?: string | null;
  surfacePlatform?: string | null;
  onSelect: () => void;
  onPin: () => void;
  onUnpin: () => void;
  onRenameStart: () => void;
  onRenameConfirm: (title: string) => void;
  onRenameCancel: () => void;
  onDelete: () => void;
  onAddToProject?: () => void;
  onChangeProject?: () => void;
  onRemoveFromProject?: () => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
}

export function ChatSidebarItem({
  title,
  meta,
  active,
  unreadCount,
  isPinned,
  isGroupChat,
  canRename,
  isMenuOpen,
  isRenaming,
  onSelect,
  onPin,
  onUnpin,
  onRenameStart,
  onRenameConfirm,
  onRenameCancel,
  onDelete,
  onAddToProject,
  onChangeProject,
  onRemoveFromProject,
  onMenuOpen,
  onMenuClose,
  projectId,
  projectName,
  surfacePlatform,
}: ChatSidebarItemProps) {
  const [renameDraft, setRenameDraft] = useState(title);
  const renameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && renameRef.current) {
      setRenameDraft(title);
      renameRef.current.focus();
      renameRef.current.select();
    }
  }, [isRenaming, title]);

  const hasUnread = unreadCount > 0;

  function handleRenameKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      const trimmed = renameDraft.trim();
      if (trimmed) onRenameConfirm(trimmed);
      else onRenameCancel();
    } else if (e.key === 'Escape') {
      onRenameCancel();
    }
  }

  function handleRenameBlur() {
    const trimmed = renameDraft.trim();
    if (trimmed) onRenameConfirm(trimmed);
    else onRenameCancel();
  }

  function handleItemClick() {
    if (!isRenaming) onSelect();
  }

  function handleItemKeyDown(e: React.KeyboardEvent) {
    if (!isRenaming && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onSelect();
    }
  }

  return (
    <div
      className={`chat-sb-item ${active ? 'active' : ''} ${hasUnread ? 'unread' : ''} ${isMenuOpen ? 'menu-open' : ''}`}
      role="button"
      tabIndex={0}
      onClick={handleItemClick}
      onKeyDown={handleItemKeyDown}
    >

      {isRenaming ? (
        <input
          ref={renameRef}
          className="sb-item-rename-input"
          value={renameDraft}
          onChange={(e) => setRenameDraft(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={handleRenameBlur}
          onClick={(e) => e.stopPropagation()}
          maxLength={60}
        />
      ) : (
        <span className="sb-item-text" title={title}>
          {surfacePlatform && surfacePlatform !== 'web' && (
            <SurfaceIcon platform={surfacePlatform} />
          )}
          {isGroupChat && (
            <svg style={{ width: 14, height: 14, marginRight: 4, flexShrink: 0, opacity: 0.6, verticalAlign: 'middle', display: 'inline-block' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Group chat">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          )}
          {title}
        </span>
      )}

      {!isRenaming && (
        <>
          {hasUnread
            ? <span className="sb-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            : <span className="sb-item-meta">{meta}</span>
          }

          <div className={`sb-item-actions ${isMenuOpen ? 'visible' : ''}`}>
            <button
              className="sb-item-actions-btn"
              type="button"
              onClick={(e) => { e.stopPropagation(); if (isMenuOpen) { onMenuClose(); } else { onMenuOpen(); } }}
              title="More actions"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.5"/>
                <circle cx="12" cy="12" r="1.5"/>
                <circle cx="12" cy="19" r="1.5"/>
              </svg>
            </button>

            {isMenuOpen && (
              <ChatContextMenu
                isPinned={isPinned}
                canRename={canRename}
                projectId={projectId}
                projectName={projectName}
                onPin={onPin}
                onUnpin={onUnpin}
                onRename={onRenameStart}
                onDelete={onDelete}
                onMoveToProject={onAddToProject ? () => onAddToProject() : undefined}
                onChangeProject={onChangeProject}
                onRemoveFromProject={onRemoveFromProject}
                onClose={onMenuClose}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Small inline SVG icon indicating conversation surface origin */
function SurfaceIcon({ platform }: { platform: string }) {
  const style = { width: 14, height: 14, marginRight: 4, flexShrink: 0, opacity: 0.6, verticalAlign: 'middle' as const, display: 'inline-block' };

  if (platform === 'slack') {
    return (
      <svg style={style} viewBox="0 0 24 24" fill="currentColor" aria-label="Slack">
        <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.124 2.521a2.528 2.528 0 0 1 2.52-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.52V8.834zm-1.271 0a2.528 2.528 0 0 1-2.521 2.521 2.528 2.528 0 0 1-2.521-2.521V2.522A2.528 2.528 0 0 1 15.166 0a2.528 2.528 0 0 1 2.521 2.522v6.312zm-2.521 10.124a2.528 2.528 0 0 1 2.521 2.52A2.528 2.528 0 0 1 15.166 24a2.528 2.528 0 0 1-2.521-2.522v-2.52h2.521zm0-1.271a2.528 2.528 0 0 1-2.521-2.521 2.528 2.528 0 0 1 2.521-2.521h6.312A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.521h-6.312z"/>
      </svg>
    );
  }

  if (platform === 'teams') {
    return (
      <svg style={style} viewBox="0 0 24 24" fill="currentColor" aria-label="Teams">
        <path d="M20.625 8.5h-3.75c.69 0 1.25-.56 1.25-1.25S17.565 6 16.875 6h.75a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0-1.922 1.078A3.74 3.74 0 0 0 12.375 1a3.75 3.75 0 0 0-3.75 3.75V6h-1.25C6.56 6 5.875 6.56 5.875 7.25S6.56 8.5 7.375 8.5H3.375c-.69 0-1.25.56-1.25 1.25v5c0 2.9 2.35 5.25 5.25 5.25h9.25c2.9 0 5.25-2.35 5.25-5.25v-5c0-.69-.56-1.25-1.25-1.25z"/>
      </svg>
    );
  }

  if (platform === 'zoom') {
    return (
      <svg style={style} viewBox="0 0 24 24" fill="currentColor" aria-label="Zoom">
        <path d="M4.585 16.56a4.585 4.585 0 0 1-4.584-4.585V7.39A4.585 4.585 0 0 1 4.585 2.807h9.17a4.585 4.585 0 0 1 4.586 4.584v4.584a4.585 4.585 0 0 1-4.585 4.585H4.585zm15.49-9.169L24 4.467v14.227l-3.925-2.925V7.39z"/>
      </svg>
    );
  }

  // ServiceNow is a ticket surface — use a ticket glyph (per the "show it's a
  // ticket" intent) rather than the brand logo.
  if (platform === 'servicenow') {
    return (
      <svg style={style} viewBox="0 0 24 24" fill="currentColor" aria-label="ServiceNow ticket">
        <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2.05a2 2 0 0 0 0 3.9V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2.05a2 2 0 0 0 0-3.9V7zm6.5 1.25a.75.75 0 0 0 0 1.5h5a.75.75 0 0 0 0-1.5h-5zm0 3a.75.75 0 0 0 0 1.5h5a.75.75 0 0 0 0-1.5h-5z"/>
      </svg>
    );
  }

  return null;
}
